import type { Dispatch, SetStateAction } from 'react';
import { Pagination } from 'antd';
import type { ITableFilters } from '../../../types';

interface InlineListPaginationProps<T extends ITableFilters> {
  total: number;
  filters: T;
  setFilters: Dispatch<SetStateAction<T>>;
  pageSizeOptions?: number[];
}

/**
 * A compact, toolbar-friendly pager for server-backed lists. Keeping it next
 * to filters and refresh actions avoids a second control row below the table.
 */
export default function InlineListPagination<T extends ITableFilters>({
  total,
  filters,
  setFilters,
  pageSizeOptions = [25, 50, 100, 200],
}: InlineListPaginationProps<T>) {
  if (total <= 0) {
    return null;
  }

  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? pageSizeOptions[0];
  const firstRecord = (page - 1) * pageSize + 1;
  const lastRecord = Math.min(page * pageSize, total);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleChange = (nextPage: number, nextPageSize: number) => {
    setFilters(previous => {
      if (
        previous.page === nextPage &&
        previous.pageSize === nextPageSize
      ) {
        return previous;
      }

      return {
        ...previous,
        page: nextPage,
        pageSize: nextPageSize,
        // A page can already exist in RTK Query's cache. Bump a client-only
        // discriminator so returning to it fetches current server data.
        paginationRevision: (previous.paginationRevision ?? 0) + 1,
      };
    });
  };

  return (
    <nav
      aria-label="List pagination"
      className="flex min-h-8 shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-1.5 py-0.5 shadow-sm"
    >
      <span className="hidden whitespace-nowrap border-r border-slate-100 px-1.5 pr-2 text-[11px] font-medium tabular-nums text-slate-500 sm:inline">
        Showing {firstRecord.toLocaleString()}-{lastRecord.toLocaleString()}{' '}
        <span className="text-slate-700">of {total.toLocaleString()}</span>
      </span>
      <span className="whitespace-nowrap px-1 text-[11px] font-medium tabular-nums text-slate-600 sm:hidden">
        {page}/{totalPages}
      </span>
      <Pagination
        size="small"
        total={total}
        current={page}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        showSizeChanger
        showQuickJumper={{ goButton: false }}
        showLessItems
        responsive
        onChange={handleChange}
        onShowSizeChange={handleChange}
        className="!m-0 !flex !items-center [&_.ant-pagination-item]:!min-w-6 [&_.ant-pagination-item]:!h-6 [&_.ant-pagination-item]:!leading-6 [&_.ant-pagination-item-active]:!border-primary-500 [&_.ant-pagination-item-active_a]:!text-primary-600 [&_.ant-pagination-jump-next]:!mx-0 [&_.ant-pagination-jump-prev]:!mx-0 [&_.ant-pagination-options]:!m-0 [&_.ant-pagination-options-size-changer]:!min-w-[72px] [&_.ant-pagination-options-size-changer_.ant-select-selector]:!h-6 [&_.ant-pagination-options-size-changer_.ant-select-selection-item]:!text-xs [&_.ant-pagination-quick-jumper]:!hidden lg:[&_.ant-pagination-quick-jumper]:!block lg:[&_.ant-pagination-quick-jumper]:!ml-1 lg:[&_.ant-pagination-quick-jumper_input]:!h-6 lg:[&_.ant-pagination-quick-jumper_input]:!w-10"
      />
    </nav>
  );
}
